import { useEffect } from 'react'
import {CheckCircle, XCircle, HelpCircle} from 'lucide-react'
import CouponCard from '../components/CouponCard'
import Newsletter from '../components/Newsletter'

export default function GlenIvyGuide() {
  useEffect(() => {
    document.title = 'Glen Ivy Coupons at Costco - Complete Guide 2025 | GlenCode'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Complete guide to finding Glen Ivy Hot Springs coupons at Costco. Compare Costco gift cards vs. Groupon vs. direct promo codes and save up to 50%.'
      )
    }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Glen Ivy Hot Springs: Complete Savings Guide
          </h1>
          <p className="text-xl text-gray-600">
            Everything you need to know about finding the best Glen Ivy coupons at Costco, Groupon, and direct booking discounts.
          </p>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Current Best Deals</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CouponCard
              title="Costco Member Exclusive"
              description="Discounted Glen Ivy gift cards available at Costco warehouse and online. Save 20% off face value."
              discount="20% OFF"
              expiryDate="While supplies last"
              verified={true}
              affiliateLink="https://www.costco.com"
            />
            
            <CouponCard
              title="Groupon Flash Deal"
              description="Limited-time spa day package including admission, towel, and lunch. Up to 50% off regular price."
              discount="50% OFF"
              code="GROUPON50"
              expiryDate="March 15, 2025"
              verified={true}
              affiliateLink="https://www.groupon.com/deals/glen-ivy"
              featured={true}
            />
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Comparison: Costco Gift Cards vs. Groupon vs. Direct Promo Codes
          </h2>
          
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold">Costco Gift Cards</th>
                    <th className="px-6 py-4 text-center font-semibold">Groupon Deals</th>
                    <th className="px-6 py-4 text-center font-semibold">Direct Promo Codes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">Average Discount</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">15-20%</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">30-50%</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">10-25%</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">Membership Required</td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mx-auto" />
                      <span className="text-sm text-gray-600">Costco Only</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <XCircle className="w-5 h-5 text-green-500 mx-auto" />
                      <span className="text-sm text-gray-600">No</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <XCircle className="w-5 h-5 text-green-500 mx-auto" />
                      <span className="text-sm text-gray-600">No</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">Booking Flexibility</td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                      <span className="text-sm text-gray-600">High</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                      <span className="text-sm text-gray-600">Limited Dates</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                      <span className="text-sm text-gray-600">High</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">Expiration</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">1-2 Years</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">3-6 Months</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Varies</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">Best For</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Costco Members</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Big Discounts</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Weekday Visits</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <p className="text-blue-900">
              <strong>Pro Tip:</strong> Stack your savings! Buy discounted Costco gift cards, then use them during off-peak weekdays with a promo code for maximum savings.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-green-300 transition">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 p-2 rounded-lg">
                  <HelpCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Does Costco sell Glen Ivy tickets?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Yes! Costco regularly sells discounted Glen Ivy Hot Springs gift cards both in-warehouse and online at Costco.com. 
                    These gift cards are typically offered at 15-20% below face value. For example, you might find a $100 gift card 
                    for $79.99. The availability can vary by location and season, so check your local Costco or the Costco website regularly.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-green-300 transition">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <HelpCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Can I use Groupon deals with Costco gift cards?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    No, Groupon deals and Costco gift cards cannot be combined. However, you can choose whichever option 
                    offers the better discount for your specific visit. Groupon typically offers deeper discounts (30-50% off) 
                    but has more restrictions on booking dates and times.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-green-300 transition">
              <div className="flex items-start gap-4">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <HelpCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    When is the best time to visit Glen Ivy for discounts?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Weekdays (Monday-Thursday) typically offer the best promo code discounts, often 20-25% off. 
                    Additionally, visiting during off-peak seasons (January-March and September-October) can yield 
                    better deals. Avoid weekends and holidays when prices are at their highest.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-green-300 transition">
              <div className="flex items-start gap-4">
                <div className="bg-yellow-100 p-2 rounded-lg">
                  <HelpCircle className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Do Glen Ivy promo codes work on weekends?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Most promotional codes are valid for weekday visits only (Monday-Friday). Weekend promo codes are rare 
                    and typically offer smaller discounts (5-10% off). For the best savings, plan your spa day during the week.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Newsletter />
        </div>
      </section>
    </div>
  )
}
